/*
  Case studies v2: rounded text selection (scoped to .cs sections).
  CSS can't round or animate ::selection, so while JS runs the native highlight is made
  transparent (html.cs-sel-js, see cs-core.css) and this draws it instead:
  - One shape per run of touching lines, rounded outside and softened at inner steps, so a
    multi-line selection reads as a single shape. Each line is trimmed to whatever box clips
    its text (e.g. the hero's reveal masks), so corners are never cut off.
  - Deliberately close to the native highlight: the same flat Trustline blue, small corner
    radius, instant on select and deselect. Only the rounded, joined shape is custom, so a
    handover to the native highlight (below) is barely noticeable.
  - Compatibility first: anything the shape can't represent cleanly gets the browser's own
    highlight back (html.cs-sel-native): select all, selections reaching outside the page's
    sections, selections spanning side-by-side boxes (cards, columns) and very large ones.
    Inputs always keep the native highlight. Phones keep
    their own selection handles and menu; only the look changes.
  The shape lives in the selection's nearest block ancestor (isolation: isolate while
  selected) at z-index -1: above that block's background, below its text.
*/
(() => {
  if (window.csSelection) return;
  window.csSelection = true;

  const NS = 'http://www.w3.org/2000/svg';
  const RADIUS = 4;
  const PAD_X = 2;
  const COLUMN_GAP = 24; // fragments on one line further apart than this are in separate boxes
  const MAX_RECTS = 400;
  document.documentElement.classList.add('cs-sel-js');

  let host = null;
  let svg = null;
  let lines = [];
  let queued = 0;
  const native = (on) => document.documentElement.classList.toggle('cs-sel-native', on);

  // Same blue as the native highlight (.cs ::selection, --cs-blue in cs-core.css)
  const MARKUP = '<path class="cs-sel__fill" fill="#1877f2"/>';

  // ---------- geometry ----------
  const blockOf = (node) => {
    let el = node.nodeType === 1 ? node : node.parentElement;
    while (el && getComputedStyle(el).display.startsWith('inline')) el = el.parentElement;
    return el;
  };

  // The visible box for a text node: its rects are trimmed to every clipping ancestor.
  const clipFor = (node, stop, cache) => {
    let box = null;
    for (let el = node.parentElement; el && el !== stop.parentElement; el = el.parentElement) {
      let clip = cache.get(el);
      if (clip === undefined) {
        const cs = getComputedStyle(el);
        clip = cs.overflowX !== 'visible' || cs.overflowY !== 'visible' ? el.getBoundingClientRect() : null;
        cache.set(el, clip);
      }
      if (clip) {
        box = box
          ? { l: Math.max(box.l, clip.left), r: Math.min(box.r, clip.right), t: Math.max(box.t, clip.top), b: Math.min(box.b, clip.bottom) }
          : { l: clip.left, r: clip.right, t: clip.top, b: clip.bottom };
      }
    }
    return box;
  };

  // One rect per selected text fragment (element boxes are skipped so whole blocks don't light up).
  const textRects = (range, block) => {
    const root = range.commonAncestorContainer;
    const cache = new Map();
    const rects = [];
    const add = (node) => {
      const r = document.createRange();
      r.selectNodeContents(node);
      if (node === range.startContainer) r.setStart(node, range.startOffset);
      if (node === range.endContainer) r.setEnd(node, range.endOffset);
      const clip = clipFor(node, block.closest('.cs') || block, cache);
      for (const rect of r.getClientRects()) {
        let l = rect.left, rr = rect.right, t = rect.top, b = rect.bottom;
        if (clip) {
          l = Math.max(l, clip.l); rr = Math.min(rr, clip.r); t = Math.max(t, clip.t); b = Math.min(b, clip.b);
        }
        if (rr - l > 0.5 && b - t > 0.5) rects.push({ left: l, right: rr, top: t, bottom: b });
      }
    };
    if (root.nodeType === 3) {
      add(root);
      return rects;
    }
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      if (n.data.trim() && range.intersectsNode(n)) add(n);
      if (rects.length > MAX_RECTS * 2) break;
    }
    return rects;
  };

  // Fragments → lines → touching groups. Returns lines tagged with their group (g), or null
  // when fragments on one line sit in separate boxes (cards, columns): no clean shape there.
  const linesFrom = (rects) => {
    const lines = [];
    let split = false;
    rects
      .slice()
      .sort((a, b) => a.top - b.top || a.left - b.left)
      .forEach((r) => {
        const mid = (r.top + r.bottom) / 2;
        const line = lines.find((l) => mid > l.t && mid < l.b);
        if (line) {
          if (r.left > line.r + COLUMN_GAP || r.right < line.l - COLUMN_GAP) split = true;
          line.l = Math.min(line.l, r.left);
          line.r = Math.max(line.r, r.right);
          line.t = Math.min(line.t, r.top);
          line.b = Math.max(line.b, r.bottom);
        } else {
          lines.push({ l: r.left, r: r.right, t: r.top, b: r.bottom });
        }
      });
    lines.sort((a, b) => a.t - b.t);
    let g = -1;
    lines.forEach((line, i) => {
      line.l -= PAD_X;
      line.r += PAD_X;
      const prev = lines[i - 1];
      const gap = prev ? line.t - prev.b : Infinity;
      const overlap = prev && line.l < prev.r && line.r > prev.l;
      if (prev && overlap && gap < (line.b - line.t) * 0.6) {
        // Close the gap between lines so they read as one shape.
        const m = (prev.b + line.t) / 2;
        prev.b = m;
        line.t = m;
      } else {
        g += 1;
      }
      line.g = g;
    });
    return split ? null : lines;
  };

  // Outline of stacked line boxes, every corner rounded (convex and concave alike).
  const pathFor = (lines) => {
    const pts = [[lines[0].l, lines[0].t], [lines[0].r, lines[0].t]];
    lines.forEach((ln, i) => {
      pts.push([ln.r, ln.b]);
      if (lines[i + 1]) pts.push([lines[i + 1].r, ln.b]);
    });
    pts.push([lines[lines.length - 1].l, lines[lines.length - 1].b]);
    for (let i = lines.length - 1; i > 0; i--) pts.push([lines[i].l, lines[i].t], [lines[i - 1].l, lines[i].t]);
    const p = pts.filter((pt, i) => {
      const q = pts[(i + pts.length - 1) % pts.length];
      return Math.abs(pt[0] - q[0]) > 0.5 || Math.abs(pt[1] - q[1]) > 0.5;
    });
    const n = p.length;
    if (n < 3) return '';
    let d = '';
    for (let i = 0; i < n; i++) {
      const a = p[(i + n - 1) % n];
      const b = p[i];
      const c = p[(i + 1) % n];
      const la = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
      const lc = Math.hypot(c[0] - b[0], c[1] - b[1]) || 1;
      const r = Math.min(RADIUS, la / 2, lc / 2);
      const s = [b[0] + ((a[0] - b[0]) * r) / la, b[1] + ((a[1] - b[1]) * r) / la];
      const e = [b[0] + ((c[0] - b[0]) * r) / lc, b[1] + ((c[1] - b[1]) * r) / lc];
      d += `${i ? 'L' : 'M'}${s[0].toFixed(1)} ${s[1].toFixed(1)}Q${b[0].toFixed(1)} ${b[1].toFixed(1)} ${e[0].toFixed(1)} ${e[1].toFixed(1)}`;
    }
    return d + 'Z';
  };

  // ---------- rendering ----------
  const render = () => {
    let d = '';
    for (let i = 0; i < lines.length; ) {
      const group = [];
      const g = lines[i].g;
      while (i < lines.length && lines[i].g === g) group.push(lines[i++]);
      d += pathFor(group);
    }
    svg.querySelectorAll('path').forEach((p) => p.setAttribute('d', d));
  };

  const release = () => {
    if (svg) {
      const old = svg;
      const oldHost = host;
      const iso = old.dataset.iso || '';
      old.remove();
      if (oldHost && !oldHost.querySelector(':scope > .cs-sel')) oldHost.style.isolation = iso;
    }
    svg = null;
    host = null;
    lines = [];
  };

  // Give the selection back to the browser's own highlight.
  const fallback = () => {
    release();
    native(true);
  };

  const draw = () => {
    queued = 0;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount) {
      native(false);
      return release();
    }
    const range = sel.getRangeAt(0);
    const block = blockOf(range.commonAncestorContainer);
    if (!block || !block.closest('.cs') || block.closest('input, textarea, select')) return fallback();

    const rects = textRects(range, block);
    if (rects.length > MAX_RECTS) return fallback();
    const found = linesFrom(rects);
    if (!found || !found.length) return fallback();

    if (host !== block) {
      release();
      host = block;
      svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('class', 'cs-sel');
      svg.dataset.iso = host.style.isolation;
      svg.innerHTML = MARKUP;
      host.style.isolation = 'isolate';
      host.appendChild(svg);
    }
    native(false);
    const origin = svg.getBoundingClientRect();
    lines = found.map((ln) => ({ g: ln.g, l: ln.l - origin.left, r: ln.r - origin.left, t: ln.t - origin.top, b: ln.b - origin.top }));
    render();
  };

  const queue = () => {
    if (!queued) queued = requestAnimationFrame(draw);
  };
  document.addEventListener('selectionchange', queue);
  window.addEventListener('resize', queue, { passive: true });
  // Re-measure when something under a held selection finishes moving (e.g. scroll reveals).
  const settle = () => svg && queue();
  document.addEventListener('transitionend', settle, true);
  document.addEventListener('animationend', settle, true);
})();
